import React, { FC, useState, useMemo } from 'react'
import {
  Button,
  Modal,
  Radio,
  Tooltip,
  Spin,
  Pagination,
  Input,
  Select,
  DatePicker,
  Form,
  FormInstance,
} from 'components/common'
import priceFormat from 'lib/str-formatter/price'
import {
  CustomerConditionType,
  CustomerQueryType,
  OrderApproval,
  OrdersQuery,
  OrdersQueryResult,
  OrdersQueryVariables,
  SortType,
  useOrdersQuery,
  useProductsLazyQuery,
} from 'lib/graphql/storeadmin'
import { formatDate } from 'helper'
import { CloseCircleFilled, InfoCircleOutlined } from '@ant-design/icons'
import { AddLedgerValues } from './AddLedgerModal'
import { usePagination } from 'lib/hooks'

type SelectBooking = {
  orderNumber: number
  name: string
  dates: string[]
  time: string
  products: string[]
  price: number
  paid: number
}

type Props = {
  selectedOrderNumber: number
  setSelectedOrderNumber: React.Dispatch<React.SetStateAction<number>>
  form: FormInstance<AddLedgerValues>
}

const defaultQueryVariables = {
  sort: { field: 'orderNumber', type: SortType.Desc },
}

export const BookingSelectModal: FC<Props> = ({
  selectedOrderNumber,
  setSelectedOrderNumber,
  form,
}) => {
  const [getProducts, { data: productsData, loading: productsLoading }] =
    useProductsLazyQuery()
  const [isOpenBookingSelectModal, setIsOpenBookingSelectModal] =
    useState(false)

  const [searchVariable, setSearchVariable] = useState<OrdersQueryVariables>({
    approval: OrderApproval.Approved,
    ...defaultQueryVariables,
  })

  const {
    data: ordersData,
    loading: ordersLoading,
    error: ordersError,
    refetch: getOrders,
    page,
    size,
    total,
    paginate,
    goto,
  } = usePagination<OrdersQuery, OrdersQueryVariables, OrdersQueryResult>({
    initialSize: 10,
    queryOptions: {
      variables: { ...searchVariable },
      fetchPolicy: 'network-only',
    },
    queryHooks: useOrdersQuery,
  })

  /* 検索条件のステート */
  const [searchOrderNumber, setSearchOrderNumber] = useState(null)
  const [searchLastName, setSearchLastName] = useState<string | null>(null)
  const [searchFirstName, setSearchFirstName] = useState<string | null>(null)
  const [searchStartDate, setSearchStartDate] = useState(null)
  const [searchEndDate, setSearchEndDate] = useState(null)
  const [searchProduct, setSearchProduct] = useState<string | null>(null)

  // 検索条件の対象施設に表示する選択肢
  const productOptions = useMemo(() => {
    if (!productsData || productsLoading) return []
    return productsData.products.edges.map(({ product }) => ({
      value: product.name.jaJp,
      label: product.name.jaJp,
    }))
  }, [productsData, productsLoading])

  // 一覧に表示するためにordersから必要なデータを抽出し整形したオブジェクトを格納したリスト
  const dataSource = useMemo<SelectBooking[]>(() => {
    return ordersData?.orders?.edges?.reduce(
      (result: SelectBooking[], { order }) => {
        const {
          orderNumber,
          customer,
          totalPrice,
          lineItems,
          bookingDates,
          period,
          payment,
        } = order
        // 利用予定日をYYYY/MM/DDの形式に変換
        let bookedDate = formatDate(bookingDates[0]?.date)
        // 利用予定日が複数日にまたがる場合は、開始日と終了日を表示
        if (
          bookingDates[0]?.date !== bookingDates[bookingDates?.length - 1]?.date
        ) {
          // 利用予定日の配列の最初（開始日）と最後（終了日）の要素を取り出し、YYYY/MM/DD ~ YYYY/MM/DDの形式に変換
          bookedDate = `${formatDate(bookingDates[0]?.date)} ~ ${formatDate(
            bookingDates[bookingDates?.length - 1]?.date
          )}`
        }
        // 予約一覧の対象施設に表示するための施設リスト（重複する施設名は削除）
        const productNames = Array.from(
          new Set(lineItems?.edges?.map(({ lineItem }) => lineItem?.name?.jaJp))
        )
        // 予約一覧に表示するためのデータを作成
        result.push({
          orderNumber: orderNumber,
          name: `${customer?.lastName} ${customer?.firstName}`,
          dates: [bookedDate],
          time: `${period?.start.slice(0, -3)} ~ ${period?.end.slice(0, -3)}`,
          products: productNames,
          price: totalPrice,
          paid: payment?.amount ?? 0,
        })
        return result
      },
      []
    ) as SelectBooking[]
  }, [ordersData])

  /**
   * モーダルを開く
   */
  const handleBookingSelectModalOpen = async () => {
    setIsOpenBookingSelectModal(true)
    // 予約情報と対象施設を並行して取得
    await Promise.all([getOrders(), getProducts()])
  }

  /**
   * キャンセルまたは×を押したときの処理
   */
  const handleBookingSelectModalClose = () => {
    // モーダルを閉じる
    setIsOpenBookingSelectModal(false)
    // 選択した予約を取り消す
    handleSelectedBookingCancel()
  }

  /**
   * 選択した予約を取り消す処理
   */
  const handleSelectedBookingCancel = () => {
    setSelectedOrderNumber(null)
    form.setFieldValue('orderNumber', null)
  }

  // 選択した予約を「入出金を追加する」モーダルに表示するためのデータ
  const selectedBooking = useMemo(() => {
    if (dataSource?.length <= 0) return null
    return dataSource?.find(
      ({ orderNumber }) => orderNumber === selectedOrderNumber
    )
  }, [dataSource, selectedOrderNumber])

  /**
   * 引数が文字列かつ全角数字の場合、半角数字に変換して返す
   * 引数が文字列かつ半角数字の場合、そのまま返す
   * 引数が文字列かつ数字以外が含まれる場合、NaNを返す
   */
  const toHalfWidth = (input: number | string): number => {
    // inputを文字列に変換
    let str = String(input)
    // 全角数字の場合、半角に変換
    if (/^[０-９]+$/.test(str)) {
      str = str.replace(/[０-９]/g, (s) =>
        String.fromCharCode(s.charCodeAt(0) - 65248)
      )
      return Number(str)
    }
    // 半角数字の場合、そのまま返す
    if (/^[0-9]+$/.test(str)) {
      return Number(str)
    }
    // 数字以外が含まれる場合
    return NaN
  }

  /**
   * 検索条件をセットして予約情報を取得する
   */
  const onSearch = async () => {
    // 開始日をYYYY-MM-DDの形式に変換
    const formattedSearchStartDate = searchStartDate
      ? searchStartDate.format('YYYY-MM-DD')
      : null
    // 終了日をYYYY-MM-DDの形式に変換
    const formattedSearchEndDate = searchEndDate
      ? searchEndDate.format('YYYY-MM-DD')
      : null

    try {
      const variables: OrdersQueryVariables = {
        approval: OrderApproval.Approved,
        orderNumber: toHalfWidth(searchOrderNumber) || null,
        customerQuery: [
          {
            condition: CustomerConditionType.Contains,
            field: 'last_name',
            type: CustomerQueryType.Or,
            value: searchLastName,
          },
          {
            condition: CustomerConditionType.Contains,
            field: 'first_name',
            type: CustomerQueryType.Or,
            value: searchFirstName,
          },
        ].filter((item) => item.value !== null && item.value !== ''),
        bookingDates:
          formattedSearchStartDate === formattedSearchEndDate
            ? formattedSearchStartDate
            : null,
        bookingDates_Gte: formattedSearchStartDate,
        bookingDates_Lte: formattedSearchEndDate,
        productName: searchProduct,
      }
      goto(1)
      setSearchVariable({ ...searchVariable, ...variables })
      await getOrders({ ...searchVariable, ...variables })
    } catch (e) {
      console.log(e)
    }
  }

  /**
   * 検索条件をクリアする
   */
  const onSearchClear = async () => {
    try {
      setSearchOrderNumber(null)
      setSearchLastName(null)
      setSearchFirstName(null)
      setSearchStartDate(null)
      setSearchEndDate(null)
      setSearchProduct(null)
      const variables = {
        approval: OrderApproval.Approved,
        ...defaultQueryVariables,
      }
      goto(1)
      setSearchVariable({ ...variables })
      await getOrders({ ...variables })
    } catch (e) {
      console.log(e)
    }
  }

  // モーダルのフッター
  const customFooter = (
    <div className="relative py-4">
      <Pagination
        style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
        }}
        total={total}
        pageSize={size}
        current={page}
        onChange={paginate}
        showSizeChanger
        pageSizeOptions={['10', '20', '50', '100']}
      />
      <div>
        <Button
          onClick={handleBookingSelectModalClose}
          style={{ marginRight: '16px' }}
        >
          キャンセル
        </Button>
        <Button
          type="primary"
          htmlType="submit"
          onClick={() => setIsOpenBookingSelectModal(false)}
        >
          決定
        </Button>
      </div>
    </div>
  )

  // 検索条件に入力があるかどうか
  const isInputSearchVariables = useMemo(() => {
    if (
      !searchOrderNumber &&
      !searchLastName &&
      !searchFirstName &&
      !searchStartDate &&
      !searchEndDate &&
      !searchProduct
    ) {
      return false
    }
    return true
  }, [
    searchOrderNumber,
    searchLastName,
    searchFirstName,
    searchStartDate,
    searchEndDate,
    searchProduct,
  ])

  return (
    <>
      <div>
        <Button type="primary" onClick={handleBookingSelectModalOpen}>
          対象の予約を{selectedOrderNumber ? '変更' : '選択'}
        </Button>
        {selectedBooking && (
          <div className="flex justify-between items-center px-4 py-2 opacity-80 bg-blue-100 rounded-sm mt-4">
            <Tooltip
              title={
                <div>
                  <div>#{selectedBooking.orderNumber}</div>
                  <div>{selectedBooking.name}</div>
                  <div>{selectedBooking.dates.map((date) => date)}</div>
                  <div>{selectedBooking.time}</div>
                  <div>
                    {selectedBooking.products
                      .map((product) => product)
                      .join(', ')}
                  </div>
                  <div>{priceFormat(selectedBooking.price)}</div>
                  <div>
                    {priceFormat(selectedBooking.price - selectedBooking.paid)}
                  </div>
                </div>
              }
            >
              <div className="flex space-x-4 text-base overflow-hidden overflow-ellipsis whitespace-nowrap pr-16">
                <div>#{selectedBooking.orderNumber}</div>
                <div>{selectedBooking.name}</div>
                <div>{selectedBooking.dates.map((date) => date)}</div>
                <div>{selectedBooking.time}</div>
                <div>
                  {selectedBooking.products
                    .map((product) => product)
                    .join(', ')}
                </div>
                <div>{priceFormat(selectedBooking.price)}</div>
                <div>
                  {priceFormat(selectedBooking.price - selectedBooking.paid)}
                </div>
              </div>
            </Tooltip>
            <div className="hover:pointer hover:opacity-60 transition duration-300">
              <CloseCircleFilled
                onClick={handleSelectedBookingCancel}
                style={{ fontSize: '16px', color: '#666' }}
              />
            </div>
          </div>
        )}
      </div>

      <Modal
        title={
          <p className="text-2xl font-bold m-0">対象の予約を選択してください</p>
        }
        width="80vw"
        style={{ maxWidth: '1200px' }}
        open={isOpenBookingSelectModal}
        bodyStyle={{ padding: '0 24px', height: '468px' }}
        onCancel={handleBookingSelectModalClose}
        footer={customFooter}
      >
        {/* 検索条件 */}
        <div className="font-bold text-xl py-2">検索条件</div>
        <div className="grid grid-cols-16 gap-x-6 mb-8">
          <Input
            allowClear
            className="col-span-2"
            placeholder="予約番号"
            value={searchOrderNumber}
            onChange={(e) => setSearchOrderNumber(e.target.value)}
            disabled={ordersLoading || productsLoading}
          />
          <div className="col-span-4 flex items-center">
            <Input
              allowClear
              placeholder="姓"
              value={searchLastName}
              onChange={(e) => setSearchLastName(e.target.value)}
              disabled={ordersLoading || productsLoading}
            />
            <Input
              allowClear
              placeholder="名"
              value={searchFirstName}
              onChange={(e) => setSearchFirstName(e.target.value)}
              disabled={ordersLoading || productsLoading}
            />
          </div>
          <div className="col-span-4 flex items-center">
            <DatePicker
              placeholder="利用開始日"
              value={searchStartDate}
              onChange={setSearchStartDate}
              disabled={ordersLoading || productsLoading}
            />
            <DatePicker
              placeholder="利用終了日"
              value={searchEndDate}
              onChange={setSearchEndDate}
              disabled={ordersLoading || productsLoading}
            />
          </div>
          <Select
            className="col-span-3"
            allowClear
            placeholder="対象施設"
            options={productOptions}
            value={searchProduct}
            onChange={setSearchProduct}
            disabled={ordersLoading || productsLoading}
          />
          <div className="col-span-3 flex items-center">
            <Button onClick={onSearch} disabled={!isInputSearchVariables}>
              絞り込む
            </Button>
            <Button onClick={onSearchClear} disabled={!isInputSearchVariables}>
              クリア
            </Button>
          </div>
        </div>

        {/* 予約一覧のヘッダー */}
        <div className="grid grid-cols-22 bg-slate-300 p-2 pr-9 font-bold text-xl">
          <div className="col-span-2">予約番号</div>
          <div className="col-span-3">名前</div>
          <div className="col-span-4">利用日</div>
          <div className="col-span-3 pl-7">利用時間帯</div>
          <div className="col-span-4">対象施設</div>
          <div className="col-span-3">請求合計金額</div>
          <div className="col-span-3 flex justify-end items-center space-x-1">
            <Tooltip
              title={
                <div className="text-lg">
                  請求合計金額から入金額を引いた金額です。
                  <br />
                  青色は<span className="text-blue-400">未入金</span>、赤色は
                  <span className="text-red-400">過入金</span>を表します。
                </div>
              }
            >
              <InfoCircleOutlined
                style={{ color: '#007bff', paddingTop: '2px' }}
              />
            </Tooltip>
            <div className="pl-px">残高</div>
          </div>
        </div>

        {/* 予約一覧のボディー */}
        {ordersError ? (
          <div className="text-center">
            予約一覧の取得時にエラーが発生しました。
          </div>
        ) : (
          <Spin spinning={ordersLoading || productsLoading} tip="Loading...">
            <Form.Item name="orderNumber">
              <Radio.Group
                buttonStyle="solid"
                optionType="button"
                value={selectedOrderNumber}
                onChange={(e) => setSelectedOrderNumber(e.target.value)}
                style={{
                  width: '100%',
                  margin: 0,
                  padding: 0,
                  height: '360px',
                  overflowY: 'scroll',
                }}
              >
                {dataSource?.length > 0
                  ? dataSource.map((data) => (
                      <Radio
                        value={data.orderNumber}
                        style={{
                          display: 'block',
                          padding: 0,
                          height: '36px',
                          lineHeight: '36px',
                        }}
                        key={data.orderNumber}
                      >
                        <div className="grid grid-cols-22 px-2">
                          <div className="col-span-2">#{data.orderNumber}</div>
                          <div className="col-span-3 overflow-hidden whitespace-nowrap overflow-ellipsis">
                            <Tooltip placement="topLeft" title={data.name}>
                              {data.name}
                            </Tooltip>
                          </div>
                          <div className="col-span-4">
                            {data.dates.map((date) => date)}
                          </div>
                          <div className="col-span-3 pl-7">{data.time}</div>
                          <div className="col-span-4 overflow-hidden whitespace-nowrap overflow-ellipsis">
                            <Tooltip
                              placement="topLeft"
                              title={data.products.map((product) => (
                                <div key={product}>{product}</div>
                              ))}
                            >
                              {data.products
                                .map((product) => product)
                                .join(', ')}
                            </Tooltip>
                          </div>
                          <div className="col-span-3 text-right overflow-hidden whitespace-nowrap overflow-ellipsis">
                            {priceFormat(data.price)}
                          </div>
                          <div className="col-span-3 text-right overflow-hidden whitespace-nowrap overflow-ellipsis">
                            <span
                              className={`
                          ${
                            data.price - data.paid === 0
                              ? 'text-green-600'
                              : data.price - data.paid > 0
                              ? 'text-blue-400'
                              : 'text-red-400'
                          }
                          ${
                            data.orderNumber === selectedOrderNumber &&
                            'text-white'
                          }`}
                            >
                              {priceFormat(data.price - data.paid)}
                            </span>
                          </div>
                        </div>
                      </Radio>
                    ))
                  : dataSource?.length <= 0 && (
                      <div className="flex items-center justify-center text-2xl h-full opacity-80">
                        対象の予約がありません
                      </div>
                    )}
              </Radio.Group>
            </Form.Item>
          </Spin>
        )}
      </Modal>
    </>
  )
}
